import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { X, Sparkles, Loader2, RefreshCw, Send, AlertCircle, Lightbulb, CheckCircle2, BookOpen, Layers, TrendingUp, Calendar, Clock, ExternalLink, Trash2, Eye, Zap, Bot, MessageCircle, Pencil } from 'lucide-react';
import { MindMapNode, Notebook } from '../types';
import { scoreNoteForThreads, generateShareAdvice, prepareDraftBatch, schedulePost, cancelScheduledPost, retryScheduledPost, updateScheduledPost, getScheduledPosts, ShareSuggestion, ScheduledPost, threadsAdvisorChat, AdvisorMessage, AdvisorNoteContext, AdvisorSelectedNote } from '../services/socialService';

interface NotebookMeta { id: string; title: string; lastPublishUrl?: string; }

interface LearningShareAdvisorModalProps {
  notebook: Notebook;
  allNotebooksMeta?: NotebookMeta[];
  language: string;
  onClose: () => void;
  onSelectDraft: (draft: { postText: string; hashtags: string[]; primaryNodeId: string | null }) => void;
  onOpenNote?: (nodeId: string) => void;
}

type NoteTab = 'recommended' | 'all' | 'unscored' | 'posted';
type MainTab = 'manager' | 'schedule';

interface LocalScore {
  score: number;
  reason: string;
  angle: string;
  assessedAt: number;
  correctnessScore?: number | null;
  correctnessAccurate?: boolean | null;
  correctnessFeedback?: string;
  angleScores?: import('../types').PostAngleScore[];
  bestAngle?: string;
  bestAngleScore?: number | null;
}

const STYLE_META = {
  diary:     { icon: <BookOpen size={11} />,   label: 'Diary',    colorClass: 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/20 dark:border-indigo-700 dark:text-indigo-300' },
  concept:   { icon: <Lightbulb size={11} />,  label: 'Concept',  colorClass: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300' },
  synthesis: { icon: <Layers size={11} />,     label: 'Synthesis',colorClass: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-300' },
  progress:  { icon: <TrendingUp size={11} />, label: 'Progress', colorClass: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300' },
} as const;

const effectiveScore = (n: MindMapNode) => n.bestAngleScore != null ? n.bestAngleScore : (n.threadsScore ?? 0);

function getDescendants(nodeId: string, allNodes: MindMapNode[]): MindMapNode[] {
  const direct = allNodes.filter(n => n.parentId === nodeId);
  return [...direct, ...direct.flatMap(c => getDescendants(c.id, allNodes))];
}

function ScoreBadge({ score }: { score?: number }) {
  if (score == null) return <span className="text-xs font-mono text-gray-300 dark:text-gray-600 w-9 text-right shrink-0">—</span>;
  const color = score >= 70 ? 'text-emerald-600 dark:text-emerald-400' : score >= 50 ? 'text-amber-500 dark:text-amber-400' : 'text-red-400 dark:text-red-500';
  return <span className={`text-sm font-bold font-mono w-9 text-right shrink-0 ${color}`}>{score}</span>;
}

function ScoreBar({ score }: { score?: number }) {
  const color = !score ? 'bg-transparent' : score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="w-9 h-1 rounded-full bg-gray-100 dark:bg-slate-700 shrink-0 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: score != null ? `${score}%` : '0%' }} />
    </div>
  );
}

export function LearningShareAdvisorModal({ notebook, allNotebooksMeta = [], language, onClose, onSelectDraft, onOpenNote }: LearningShareAdvisorModalProps) {
  const isVi = language === 'vietnamese';

  // Active notebook state — starts with the prop notebook, can switch to others
  const [activeNotebookMeta, setActiveNotebookMeta] = useState<NotebookMeta>({
    id: notebook.id, title: notebook.title, lastPublishUrl: (notebook as any).lastPublishUrl,
  });
  const [loadedNodes, setLoadedNodes] = useState<MindMapNode[]>(notebook.nodes);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);

  const loadNotebook = useCallback(async (meta: NotebookMeta) => {
    if (meta.id === activeNotebookMeta.id) return;
    setActiveNotebookMeta(meta);
    setLoadedNodes([]);
    setSelectedBranchId(null);
    setCheckedBranchIds(new Set());
    setCheckedNoteIds(new Set());
    setLocalScores({});
    setLocalDrafts({});
    setIsLoadingNodes(true);
    try {
      const uid = getAuth().currentUser?.uid;
      if (!uid) return;
      const db = getFirestore();
      const snap = await getDocs(collection(db, `users/${uid}/notebooks/${meta.id}/nodes`));
      const nodes: MindMapNode[] = snap.docs.map(d => ({ id: d.id, ...d.data() } as MindMapNode));
      setLoadedNodes(nodes);
      // Also sync drafts for this notebook
      const next: Record<string, { postDraft: string; hashtags: string[]; style: string; at: number }> = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.threadsPostDraft && data.threadsPostDraftAt) {
          next[d.id] = { postDraft: data.threadsPostDraft, hashtags: data.threadsPostDraftHashtags || [], style: data.threadsPostDraftStyle || 'concept', at: data.threadsPostDraftAt };
        }
      });
      setLocalDrafts(next);
    } catch {}
    setIsLoadingNodes(false);
  }, [activeNotebookMeta.id]);

  // Navigation state
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [noteTab, setNoteTab] = useState<NoteTab>('all');

  // Checkbox multi-select for scoring
  const [checkedBranchIds, setCheckedBranchIds] = useState<Set<string>>(new Set());

  // Checkbox multi-select for notes (for per-note draft prep)
  const [checkedNoteIds, setCheckedNoteIds] = useState<Set<string>>(new Set());

  // Scoring progress
  const [isRescoring, setIsRescoring] = useState(false);
  const [rescoreProgress, setRescoreProgress] = useState({ done: 0, total: 0 });
  const [rescoreError, setRescoreError] = useState<string | null>(null);
  const [scoringNodeId, setScoringNodeId] = useState<string | null>(null);

  // Local scores (immediate update, no waiting for Firestore → props pipeline)
  const [localScores, setLocalScores] = useState<Record<string, LocalScore>>({});

  // Batch draft preparation
  const DRAFT_JOB_KEY = `threads_draft_job_${activeNotebookMeta.id}`;
  const readDraftJob = () => {
    try { return JSON.parse(localStorage.getItem(DRAFT_JOB_KEY) || 'null') as { total: number; startedAt: number } | null; } catch { return null; }
  };
  const cloudJob = readDraftJob();
  const cloudJobAge = cloudJob ? Date.now() - cloudJob.startedAt : Infinity;
  const isCloudRunning = cloudJob !== null && cloudJobAge < 540_000; // still within Cloud Function timeout

  const [isPreparing, setIsPreparing] = useState(false);
  const [prepareProgress, setPrepareProgress] = useState({ done: 0, total: 0 });
  const [preparingNodeId, setPreparingNodeId] = useState<string | null>(null);
  // Local drafts (synced to node after prepareDraftBatch returns from Firestore)
  const [localDrafts, setLocalDrafts] = useState<Record<string, { postDraft: string; hashtags: string[]; style: string; at: number }>>({});

  // Main tab
  const [mainTab, setMainTab] = useState<MainTab>('manager');

  // Scheduled posts tab
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [editScheduleValue, setEditScheduleValue] = useState('');
  const [savingScheduleId, setSavingScheduleId] = useState<string | null>(null);
  const [previewPost, setPreviewPost] = useState<ScheduledPost | null>(null);

  // Draft state
  const [draftNode, setDraftNode] = useState<MindMapNode | null>(null);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [draftSuggestions, setDraftSuggestions] = useState<ShareSuggestion[] | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(0);

  // Inline schedule state (inside draft panel)
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [editedText, setEditedText] = useState('');

  // Advisor chat state
  const [advisorMessages, setAdvisorMessages] = useState<AdvisorMessage[]>([]);
  const [advisorInput, setAdvisorInput] = useState('');
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const advisorBottomRef = useRef<HTMLDivElement>(null);
  const advisorInputRef = useRef<HTMLTextAreaElement>(null);

  const allNodes = useMemo(() => loadedNodes, [loadedNodes]);

  const nodeIds = useMemo(() => new Set(allNodes.map(n => n.id)), [allNodes]);
  const rootNode = useMemo(() =>
    allNodes.find(n => !n.parentId || !nodeIds.has(n.parentId)),
    [allNodes, nodeIds]
  );

  const branches = useMemo(() => {
    if (!rootNode) return [];
    return allNodes
      .filter(n => n.parentId === rootNode.id)
      .sort((a, b) =>
        (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' })
      );
  }, [allNodes, rootNode]);

  // Auto-select first branch when Manager opens or notebook changes
  useEffect(() => {
    if (branches.length > 0 && !selectedBranchId) {
      setSelectedBranchId(branches[0].id);
    }
  }, [branches]);

  // Resolve GitHub link for the current draftNode
  const [linkInFirstComment, setLinkInFirstComment] = useState(true);

  const resolvedNoteLink = useMemo(() => {
    if (!draftNode) return null;
    const pub = activeNotebookMeta.lastPublishUrl;
    if (!pub) return null;
    const findChapter = (nodeId: string): MindMapNode | null => {
      const node = allNodes.find(n => n.id === nodeId);
      if (!node) return null;
      if (!node.parentId || !allNodes.find(n => n.id === node.parentId)?.parentId) return node;
      return findChapter(node.parentId);
    };
    const chapter = findChapter(draftNode.id);
    const blobUrl = pub.replace('/tree/', '/blob/');
    if (!chapter) return blobUrl;
    const slug = (chapter.title || 'untitled').replace(/[^\w\s-]/g, '').trim().replace(/[-\s]+/g, '_').toLowerCase() || 'untitled';
    return `${blobUrl}/${slug}.md#node-${draftNode.id}`;
  }, [draftNode, notebook, allNodes]);

  const draftNodeImageUrl = useMemo(() => {
    if (!draftNode) return null;
    const img = draftNode.imageBase64;
    if (img && img.startsWith('https://')) return img;
    return draftNode.attachments?.find(a => a.type === 'image' && a.content?.startsWith('https://'))?.content ?? null;
  }, [draftNode]);

  // Merge local scores + local drafts with props data
  const enhanceNode = useCallback((node: MindMapNode): MindMapNode => {
    const local = localScores[node.id];
    const draft = localDrafts[node.id];
    let enhanced = local
      ? { ...node, threadsScore: local.score, threadsScoreReason: local.reason, threadsScoreAngle: local.angle, threadsAssessedAt: local.assessedAt, correctnessScore: local.correctnessScore, correctnessAccurate: local.correctnessAccurate, correctnessFeedback: local.correctnessFeedback, angleScores: local.angleScores, bestAngle: local.bestAngle, bestAngleScore: local.bestAngleScore ?? undefined }
      : node;
    if (draft) {
      enhanced = { ...enhanced, threadsPostDraft: draft.postDraft, threadsPostDraftHashtags: draft.hashtags, threadsPostDraftStyle: draft.style, threadsPostDraftAt: draft.at };
    }
    return enhanced;
  }, [localScores, localDrafts]);

  const branchDescendants = useMemo(() => {
    const map = new Map<string, MindMapNode[]>();
    for (const b of branches) {
      map.set(b.id, getDescendants(b.id, allNodes).filter(n => n.parentId != null).map(enhanceNode));
    }
    return map;
  }, [branches, allNodes, enhanceNode]);

  const selectedBranch = useMemo(() =>
    selectedBranchId ? branches.find(b => b.id === selectedBranchId) ?? null : null,
    [selectedBranchId, branches]
  );

  const branchNotes = useMemo(() => {
    if (!selectedBranchId) return [];
    return branchDescendants.get(selectedBranchId) ?? [];
  }, [selectedBranchId, branchDescendants]);

  const scoredNotes   = useMemo(() => branchNotes.filter(n => n.threadsAssessedAt != null), [branchNotes]);
  const recommended   = useMemo(() => scoredNotes.filter(n => effectiveScore(n) >= 60).sort((a, b) => effectiveScore(b) - effectiveScore(a)), [scoredNotes]);
  const unscoredNotes = useMemo(() => branchNotes.filter(n => !n.threadsAssessedAt), [branchNotes]);
  const postedNotes   = useMemo(() => branchNotes.filter(n => n.threadsPostedAt), [branchNotes]);

  const displayNotes = useMemo(() => {
    switch (noteTab) {
      case 'recommended': return recommended;
      case 'unscored':    return unscoredNotes;
      case 'posted':      return postedNotes;
      default:
        return [...scoredNotes.sort((a, b) => effectiveScore(b) - effectiveScore(a)), ...unscoredNotes];
    }
  }, [noteTab, recommended, unscoredNotes, postedNotes, scoredNotes]);

  // Score a list of notes one by one with live progress
  const scoreNotes = useCallback(async (toScore: MindMapNode[]) => {
    if (!toScore.length) return;
    setIsRescoring(true);
    setRescoreError(null);
    setRescoreProgress({ done: 0, total: toScore.length });

    for (let i = 0; i < toScore.length; i++) {
      const note = toScore[i];
      setScoringNodeId(note.id);
      try {
        const result = await scoreNoteForThreads({
          notebookId: activeNotebookMeta.id,
          nodeId: note.id,
          title: note.title || '',
          content: note.content || '',
          language,
        });
        setLocalScores(prev => ({
          ...prev,
          [note.id]: { score: result.score, reason: result.reason, angle: result.bestAngle || '', assessedAt: Date.now(), correctnessScore: result.correctnessScore, correctnessAccurate: result.correctnessAccurate, correctnessFeedback: result.correctnessFeedback, angleScores: result.angleScores, bestAngle: result.bestAngle, bestAngleScore: result.bestAngleScore },
        }));
      } catch (e: any) {
        // skip failed notes silently
      }
      setRescoreProgress({ done: i + 1, total: toScore.length });
    }

    setScoringNodeId(null);
    setIsRescoring(false);
  }, [activeNotebookMeta.id, language]);

  const needsScoring = (n: MindMapNode) => !n.threadsAssessedAt || n.correctnessScore == null;

  const handleScoreBranch = useCallback((branchId: string) => {
    const notes = (branchDescendants.get(branchId) ?? []).filter(needsScoring);
    scoreNotes(notes);
  }, [branchDescendants, scoreNotes]);

  const handleScoreChecked = useCallback(() => {
    const toScore: MindMapNode[] = [];
    for (const branchId of checkedBranchIds) {
      const notes = (branchDescendants.get(branchId) ?? []).filter(needsScoring);
      toScore.push(...notes);
    }
    scoreNotes(toScore);
  }, [checkedBranchIds, branchDescendants, scoreNotes]);

  const handleScoreAll = useCallback(() => {
    const toScore = allNodes.filter(n => n.parentId != null).map(enhanceNode).filter(needsScoring);
    scoreNotes(toScore);
  }, [allNodes, enhanceNode, scoreNotes]);

  const toggleBranchCheck = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckedBranchIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const totalUnscoredAll = useMemo(() =>
    allNodes.filter(n => n.parentId != null && needsScoring(enhanceNode(n))).length,
    [allNodes, enhanceNode]
  );

  const checkedUnscoredCount = useMemo(() => {
    let count = 0;
    for (const id of checkedBranchIds) {
      count += (branchDescendants.get(id) ?? []).filter(needsScoring).length;
    }
    return count;
  }, [checkedBranchIds, branchDescendants]);

  const checkedPreparableCount = useMemo(() => {
    let count = 0;
    for (const id of checkedBranchIds) {
      count += (branchDescendants.get(id) ?? []).filter(n =>
        effectiveScore(n) >= 60 &&
        (!n.threadsPostDraftAt || Date.now() - n.threadsPostDraftAt > DRAFT_TTL_MS)
      ).length;
    }
    return count;
  }, [checkedBranchIds, branchDescendants]);

  const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  const handleGenerateDraft = useCallback(async (node: MindMapNode) => {
    setDraftNode(node);
    setDraftSuggestions(null);
    setDraftError(null);

    // Use cached draft if fresh
    const draftAge = node.threadsPostDraftAt ? Date.now() - node.threadsPostDraftAt : Infinity;
    if (node.threadsPostDraft && draftAge < DRAFT_TTL_MS) {
      const style = (node.threadsPostDraftStyle || 'concept') as keyof typeof STYLE_META;
      const synth: ShareSuggestion = {
        style: style in STYLE_META ? style as ShareSuggestion['style'] : 'concept',
        title: node.title || '',
        postText: node.threadsPostDraft,
        includedNodeIds: [node.id],
        primaryNodeId: node.id,
        reason: isVi ? 'Bản thảo đã được chuẩn bị trước.' : 'Pre-generated draft.',
        hashtags: node.threadsPostDraftHashtags || [],
        confidence: 'high',
      };
      setDraftSuggestions([synth]);
      return;
    }

    setIsGeneratingDraft(true);
    try {
      const bestAngleData = node.angleScores?.find(a => a.angle === node.bestAngle);
      const result = await generateShareAdvice({
        notes: [{
          id: node.id, title: node.title || '', content: node.content || '',
          ...(node.bestAngle ? { bestAngle: node.bestAngle, bestAngleScore: node.bestAngleScore, suggestedHook: bestAngleData?.suggestedHook } : {}),
        }],
        notebookTitle: activeNotebookMeta.title,
        lastPublishUrl: activeNotebookMeta.lastPublishUrl,
        language,
      });
      setDraftSuggestions(result.suggestions || []);
    } catch (e: any) {
      setDraftError(e.message || 'Failed');
    } finally {
      setIsGeneratingDraft(false);
    }
  }, [notebook, language, isVi]);

  const handlePrepareDrafts = useCallback(async () => {
    const toPrepare: MindMapNode[] = [];
    for (const branchId of checkedBranchIds) {
      const notes = (branchDescendants.get(branchId) ?? []).filter(n =>
        effectiveScore(n) >= 60 &&
        (!n.threadsPostDraftAt || Date.now() - n.threadsPostDraftAt > DRAFT_TTL_MS)
      );
      toPrepare.push(...notes);
    }
    if (!toPrepare.length) return;

    localStorage.setItem(DRAFT_JOB_KEY, JSON.stringify({ total: toPrepare.length, startedAt: Date.now() }));
    setIsPreparing(true);
    setPrepareProgress({ done: 0, total: toPrepare.length });

    try {
      const result = await prepareDraftBatch({
        notebookId: activeNotebookMeta.id,
        notes: toPrepare.map(n => ({ id: n.id, title: n.title || '', content: n.content || '', threadsPostDraftAt: n.threadsPostDraftAt })),
        notebookTitle: activeNotebookMeta.title,
        language,
      });
      localStorage.removeItem(DRAFT_JOB_KEY);
      const now = Date.now();
      setLocalDrafts(prev => {
        const next = { ...prev };
        for (const n of toPrepare) {
          if (!next[n.id]) next[n.id] = { postDraft: '__pending__', hashtags: [], style: 'concept', at: now };
        }
        return next;
      });
      setPrepareProgress({ done: result.processed, total: toPrepare.length });
    } catch { localStorage.removeItem(DRAFT_JOB_KEY); }

    setIsPreparing(false);
  }, [checkedBranchIds, branchDescendants, notebook, language, DRAFT_JOB_KEY]);

  // Prepare drafts for individually checked notes
  const handlePrepareCheckedNotes = useCallback(async () => {
    const toPrepare = branchNotes.filter(n =>
      checkedNoteIds.has(n.id) &&
      (!n.threadsPostDraftAt || Date.now() - n.threadsPostDraftAt > DRAFT_TTL_MS)
    );
    if (!toPrepare.length) return;

    localStorage.setItem(DRAFT_JOB_KEY, JSON.stringify({ total: toPrepare.length, startedAt: Date.now() }));
    setIsPreparing(true);
    setPrepareProgress({ done: 0, total: toPrepare.length });

    try {
      const result = await prepareDraftBatch({
        notebookId: activeNotebookMeta.id,
        notes: toPrepare.map(n => ({ id: n.id, title: n.title || '', content: n.content || '', threadsPostDraftAt: n.threadsPostDraftAt })),
        notebookTitle: activeNotebookMeta.title,
        language,
      });
      localStorage.removeItem(DRAFT_JOB_KEY);
      const now = Date.now();
      setLocalDrafts(prev => {
        const next = { ...prev };
        for (const n of toPrepare) {
          if (!next[n.id]) next[n.id] = { postDraft: '__pending__', hashtags: [], style: 'concept', at: now };
        }
        return next;
      });
      setPrepareProgress({ done: result.processed, total: toPrepare.length });
    } catch { localStorage.removeItem(DRAFT_JOB_KEY); }

    setIsPreparing(false);
    setCheckedNoteIds(new Set());
  }, [checkedNoteIds, branchNotes, notebook, language, DRAFT_JOB_KEY]);

  const loadScheduledPosts = useCallback(async () => {
    setLoadingScheduled(true);
    try {
      const posts = await getScheduledPosts();
      setScheduledPosts(posts);
    } catch {}
    setLoadingScheduled(false);
  }, []);

  useEffect(() => {
    if (mainTab === 'schedule') loadScheduledPosts();
  }, [mainTab, loadScheduledPosts]);

  // Auto-refresh when user switches back to this browser tab
  useEffect(() => {
    if (mainTab !== 'schedule') return;
    const onVisible = () => { if (document.visibilityState === 'visible') loadScheduledPosts(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mainTab, loadScheduledPosts]);

  useEffect(() => {
    const text = draftSuggestions?.[selectedSuggestionIdx]?.postText ?? '';
    setEditedText(text);
  }, [selectedSuggestionIdx, draftSuggestions]);

  // Sync localDrafts from Firestore — called on mount and when cloud job finishes
  const syncDraftsFromFirestore = useCallback(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    const db = getFirestore();
    getDocs(collection(db, `users/${uid}/notebooks/${activeNotebookMeta.id}/nodes`)).then(snap => {
      setLocalDrafts(prev => {
        const next = { ...prev };
        snap.forEach(doc => {
          const d = doc.data();
          if (d.threadsPostDraft && d.threadsPostDraftAt) {
            next[doc.id] = {
              postDraft: d.threadsPostDraft,
              hashtags: d.threadsPostDraftHashtags || [],
              style: d.threadsPostDraftStyle || 'concept',
              at: d.threadsPostDraftAt,
            };
          }
        });
        return next;
      });
    }).catch(() => {});
  }, [activeNotebookMeta.id]);

  // On mount: sync drafts (catches jobs that finished while modal was closed)
  useEffect(() => { syncDraftsFromFirestore(); }, []);

  // When cloud job finishes (isCloudRunning flips false), sync immediately
  const prevCloudRunning = useRef(isCloudRunning);
  useEffect(() => {
    const justFinished = prevCloudRunning.current && !isCloudRunning;
    prevCloudRunning.current = isCloudRunning;
    if (justFinished) syncDraftsFromFirestore();
  }, [isCloudRunning, syncDraftsFromFirestore]);

  const handleCancelPost = useCallback(async (postId: string) => {
    setCancellingId(postId);
    setConfirmCancelId(null);
    try {
      await cancelScheduledPost(postId);
      setScheduledPosts(prev => prev.filter(p => p.id !== postId));
    } catch {}
    setCancellingId(null);
  }, []);

  const handleRetryPost = useCallback(async (postId: string) => {
    setRetryingId(postId);
    try {
      await retryScheduledPost(postId);
      setScheduledPosts(prev => prev.map(p => p.id === postId
        ? { ...p, status: 'pending', scheduledAt: Date.now() + 2 * 60 * 1000, failReason: undefined, failedAt: undefined }
        : p
      ));
    } catch {}
    setRetryingId(null);
  }, []);

  const handleStartEditSchedule = useCallback((post: ScheduledPost) => {
    // Format scheduledAt to datetime-local value
    const d = new Date(post.scheduledAt);
    const pad = (n: number) => String(n).padStart(2, '0');
    const val = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setEditScheduleValue(val);
    setEditingScheduleId(post.id);
  }, []);

  const handleSaveSchedule = useCallback(async (postId: string) => {
    if (!editScheduleValue) return;
    const newTs = new Date(editScheduleValue).getTime();
    if (newTs <= Date.now()) return;
    setSavingScheduleId(postId);
    try {
      await updateScheduledPost(postId, newTs);
      setScheduledPosts(prev => prev.map(p => p.id === postId ? { ...p, scheduledAt: newTs } : p));
      setEditingScheduleId(null);
    } catch {}
    setSavingScheduleId(null);
  }, [editScheduleValue]);

  const handleSchedule = useCallback(async (suggestion: ShareSuggestion) => {
    setScheduleError(null);
    if (!scheduleDateTime || !draftNode) {
      setScheduleError(isVi ? 'Vui lòng chọn thời gian đăng.' : 'Please pick a posting time.');
      return;
    }
    const scheduledAt = new Date(scheduleDateTime).getTime();
    if (scheduledAt <= Date.now()) {
      setScheduleError(isVi ? 'Thời gian phải ở tương lai (ít nhất 5 phút).' : 'Time must be at least 5 minutes in the future.');
      return;
    }
    const imageUrls: string[] = draftNodeImageUrl ? [draftNodeImageUrl] : [];
    setIsScheduling(true);
    try {
      await schedulePost({
        nodeId: draftNode.id,
        notebookId: activeNotebookMeta.id,
        platform: 'threads',
        postText: editedText,
        hashtags: suggestion.hashtags || [],
        imageUrls,
        noteLink: (!linkInFirstComment && resolvedNoteLink) ? resolvedNoteLink : undefined,
        firstCommentLink: (linkInFirstComment && resolvedNoteLink) ? resolvedNoteLink : undefined,
        scheduledAt,
      });
      setScheduleSuccess(true);
      setTimeout(() => { setScheduleSuccess(false); setScheduleDateTime(''); }, 3000);
    } catch (e: any) {
      setScheduleError(e.message || 'Failed to schedule');
    }
    setIsScheduling(false);
  }, [scheduleDateTime, draftNode, activeNotebookMeta.id, resolvedNoteLink, draftNodeImageUrl, isVi, editedText]);

  // --- Draft panel ---
  if (draftNode) {
    const selectedS = draftSuggestions?.[selectedSuggestionIdx] ?? null;
    const hashtagStr = (selectedS?.hashtags || []).map((h: string) => h.startsWith('#') ? h : `#${h}`).join(' ');
    const totalChars = selectedS ? [editedText, linkInFirstComment ? null : resolvedNoteLink, hashtagStr].filter(Boolean).join('\n\n').length : 0;
    const confColor = (c?: string) => c === 'high' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : c === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';

    return (
      <div className="fixed inset-0 z-[200] bg-white dark:bg-slate-900 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setDraftNode(null); setDraftSuggestions(null); setSelectedSuggestionIdx(0); setScheduleSuccess(false); setScheduleError(null); }}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex items-center gap-1"
            >
              ← {isVi ? 'Quay lại' : 'Back'}
            </button>
            <div className="h-4 w-px bg-gray-200 dark:bg-slate-700" />
            <p className="font-semibold text-sm dark:text-white truncate max-w-md">{draftNode.title || '(untitled)'}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg">
            <X size={18} />
          </button>
        </div>

        {/* Loading */}
        {isGeneratingDraft && (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 text-gray-400">
            <Loader2 size={28} className="animate-spin text-indigo-500" />
            <p className="text-sm">{isVi ? 'AI đang tạo bản thảo...' : 'Generating draft...'}</p>
          </div>
        )}

        {/* Error */}
        {!isGeneratingDraft && draftError && (
          <div className="flex-1 p-6">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-600 dark:text-red-400">{draftError}</div>
          </div>
        )}

        {/* Empty */}
        {!isGeneratingDraft && draftSuggestions?.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400">
            <AlertCircle size={28} />
            <p className="text-sm">{isVi ? 'Không tìm thấy góc độ phù hợp.' : 'No suitable post angle found.'}</p>
            <button onClick={() => onSelectDraft({ postText: '', hashtags: [], primaryNodeId: draftNode.id })} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg">
              {isVi ? 'Mở trình soạn thảo' : 'Open post editor'}
            </button>
          </div>
        )}

        {/* Two-column layout */}
        {!isGeneratingDraft && draftSuggestions && draftSuggestions.length > 0 && (
          <div className="flex flex-1 overflow-hidden">

            {/* Left — style chip list */}
            <div className="w-52 shrink-0 border-r dark:border-slate-700 flex flex-col overflow-y-auto py-3 gap-1 bg-gray-50/50 dark:bg-slate-950/50">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-4 pb-1">
                {isVi ? 'Phong cách' : 'Style'}
              </p>
              {draftSuggestions.map((s, i) => {
                const meta = STYLE_META[s.style as keyof typeof STYLE_META] || STYLE_META.diary;
                const isActive = i === selectedSuggestionIdx;
                return (
                  <button
                    key={i}
                    onClick={() => { setSelectedSuggestionIdx(i); setScheduleSuccess(false); setScheduleError(null); }}
                    className={`mx-2 px-3 py-2.5 rounded-xl text-left transition-all border ${
                      isActive
                        ? 'bg-white dark:bg-slate-800 border-indigo-200 dark:border-indigo-700/60 shadow-sm'
                        : 'border-transparent hover:bg-white/70 dark:hover:bg-slate-800/40'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.colorClass}`}>{meta.icon}{meta.label}</span>
                      <span className={`text-[9px] font-bold ${s.confidence === 'high' ? 'text-emerald-500' : s.confidence === 'medium' ? 'text-amber-500' : 'text-gray-400'}`}>{s.confidence?.toUpperCase()}</span>
                    </div>
                    <p className={`text-[11px] leading-snug line-clamp-2 ${isActive ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>{s.title}</p>
                  </button>
                );
              })}
            </div>

            {/* Right — single selected card */}
            {selectedS && (
              <div className="flex-1 flex flex-col overflow-hidden">

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="max-w-2xl mx-auto space-y-4">

                    {/* Title + confidence */}
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="font-bold text-xl dark:text-white leading-tight">{selectedS.title}</h2>
                      <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full ${confColor(selectedS.confidence)}`}>{selectedS.confidence?.toUpperCase()}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed -mt-2">{selectedS.reason}</p>

                    {/* Score row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Postworthy */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{isVi ? 'Đáng post' : 'Postworthy'}</span>
                        {draftNode.threadsScore != null
                          ? <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded-full ${draftNode.threadsScore >= 70 ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : draftNode.threadsScore >= 50 ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400' : 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400'}`}>{draftNode.threadsScore}</span>
                          : <span className="text-[10px] text-gray-300 dark:text-gray-600 italic">{isVi ? 'chưa score' : 'unscored'}</span>
                        }
                      </div>
                      <div className="w-px h-3 bg-gray-200 dark:bg-slate-700" />
                      {/* Correctness */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Correctness</span>
                        {draftNode.correctnessScore != null
                          ? <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded-full flex items-center gap-1 ${draftNode.correctnessAccurate ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400'}`}>
                              {draftNode.correctnessAccurate ? '✓' : '✗'} {draftNode.correctnessScore}
                            </span>
                          : <span className="text-[10px] text-gray-300 dark:text-gray-600 italic">{isVi ? 'chưa score' : 'unscored'}</span>
                        }
                      </div>
                    </div>

                    {/* Warning banner */}
                    {(draftNode.correctnessScore == null || draftNode.correctnessAccurate === false) && (
                      <div className={`flex items-start gap-2 px-3 py-2.5 rounded-xl text-xs border ${draftNode.correctnessScore == null ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800/50 text-amber-700 dark:text-amber-400' : 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800/50 text-red-600 dark:text-red-400'}`}>
                        <AlertCircle size={13} className="shrink-0 mt-0.5" />
                        <span>
                          {draftNode.correctnessScore == null
                            ? (isVi ? 'Chưa có Correctness score — chạy scoring để AI kiểm tra nội dung trước khi post.' : 'No correctness score yet — run scoring so AI can verify the content.')
                            : (isVi ? `Correctness thấp: ${draftNode.correctnessFeedback || 'Có thể có lỗi nội dung. Xem lại trước khi post.'}` : `Low correctness: ${draftNode.correctnessFeedback || 'Possible content errors. Review before posting.'}`)
                          }
                        </span>
                      </div>
                    )}

                    {/* Post text — editable */}
                    <div className="relative">
                      <textarea
                        value={editedText}
                        onChange={e => setEditedText(e.target.value)}
                        rows={6}
                        className="w-full bg-gray-50 dark:bg-slate-800 rounded-xl p-4 border dark:border-slate-700 text-sm text-gray-800 dark:text-gray-200 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className={`absolute bottom-2 right-3 text-[10px] font-mono font-semibold ${totalChars > 500 ? 'text-red-500' : totalChars > 450 ? 'text-amber-500' : 'text-gray-400'}`}>
                        {totalChars} / 500
                      </span>
                    </div>

                    {/* Hashtags */}
                    {selectedS.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedS.hashtags.map((h: string, j: number) => (
                          <span key={j} className="text-[11px] text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2.5 py-0.5 rounded-full font-medium">{h.startsWith('#') ? h : `#${h}`}</span>
                        ))}
                      </div>
                    )}

                    {/* Note link */}
                    {resolvedNoteLink ? (
                      <div className="border dark:border-slate-700 rounded-xl overflow-hidden">
                        <a href={resolvedNoteLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group">
                          <ExternalLink size={12} className="text-indigo-500 shrink-0" />
                          <span className="text-[11px] text-indigo-600 dark:text-indigo-400 truncate group-hover:underline flex-1">{resolvedNoteLink}</span>
                        </a>
                        <button
                          onClick={() => setLinkInFirstComment(v => !v)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-[10px] border-t dark:border-slate-700 transition-colors ${linkInFirstComment ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400' : 'bg-white dark:bg-slate-800 text-gray-400 dark:text-gray-500'}`}
                        >
                          <div className={`w-3 h-3 rounded-full border-2 shrink-0 ${linkInFirstComment ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 dark:border-slate-500'}`} />
                          {isVi ? (linkInFirstComment ? 'Đăng link vào comment đầu tiên ✓' : 'Đăng link trong bài chính') : (linkInFirstComment ? 'Post link as first comment ✓' : 'Include link in post body')}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 border dark:border-slate-700 rounded-xl bg-gray-50 dark:bg-slate-800">
                        <ExternalLink size={12} className="text-gray-300 dark:text-gray-600 shrink-0" />
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 italic">{isVi ? 'Notebook chưa publish lên GitHub' : 'Notebook not published to GitHub'}</span>
                      </div>
                    )}

                    {/* Screenshot */}
                    {draftNodeImageUrl && (
                      <div className="rounded-xl overflow-hidden border dark:border-slate-700">
                        <img src={draftNodeImageUrl} alt="" className="w-full object-cover max-h-64" />
                      </div>
                    )}

                  </div>
                </div>

                {/* Sticky action footer */}
                <div className="shrink-0 border-t dark:border-slate-700 bg-white dark:bg-slate-900 p-4 max-w-none">
                  <div className="max-w-2xl mx-auto space-y-3">

                    {/* Schedule row */}
                    {scheduleSuccess ? (
                      <div className="flex items-center gap-2.5 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={16} />
                        <p className="text-sm font-semibold">{isVi ? 'Đã lên lịch thành công!' : 'Scheduled successfully!'}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{isVi ? 'Lên lịch tự động đăng' : 'Auto-post schedule'}</p>
                        <div className="flex items-center gap-2">
                          <input
                            type="datetime-local"
                            value={scheduleDateTime}
                            onChange={e => { setScheduleDateTime(e.target.value); setScheduleError(null); }}
                            min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                            className="flex-1 px-3 py-1.5 border dark:border-slate-600 rounded-lg text-xs dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          <button
                            onClick={() => handleSchedule(selectedS)}
                            disabled={!scheduleDateTime || isScheduling || totalChars > 500}
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors shrink-0"
                          >
                            {isScheduling ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />}
                            {isVi ? 'Lên lịch' : 'Schedule'}
                          </button>
                        </div>
                        {scheduleError && <p className="text-xs text-red-500 dark:text-red-400">{scheduleError}</p>}
                        {totalChars > 500 && <p className="text-xs text-red-500 dark:text-red-400">{isVi ? `Vượt giới hạn ${totalChars - 500} ký tự — hãy chỉnh sửa nội dung trước.` : `${totalChars - 500} chars over limit — edit the text above.`}</p>}
                      </div>
                    )}

                    {/* Post now */}
                    <button
                      onClick={() => onSelectDraft({ postText: editedText, hashtags: selectedS.hashtags || [], primaryNodeId: selectedS.primaryNodeId || draftNode.id })}
                      className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                    >
                      <Send size={14} />
                      {isVi ? 'Post ngay' : 'Post now'}
                    </button>

                  </div>
                </div>

              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- Manager Board ---
  const noteTabs: { key: NoteTab; label: string; count: number }[] = [
    { key: 'recommended', label: isVi ? 'Đề xuất' : 'Top',      count: recommended.length },
    { key: 'all',         label: isVi ? 'Tất cả' : 'All',       count: branchNotes.length },
    { key: 'unscored',    label: isVi ? 'Chưa score' : 'Unscored', count: unscoredNotes.length },
    { key: 'posted',      label: isVi ? 'Đã post' : 'Posted',   count: postedNotes.length },
  ];

  const allBranchesChecked = branches.length > 0 && branches.every(b => checkedBranchIds.has(b.id));
  const someBranchesChecked = checkedBranchIds.size > 0;

  return (
    <div className="fixed inset-0 z-[200] bg-white dark:bg-slate-900 flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-700 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-white" />
          </div>
          <div>
            <h2 className="font-bold text-base dark:text-white leading-tight">Threads Manager</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-xs">{activeNotebookMeta.title}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isRescoring ? (
            <div className="flex items-center gap-2 text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5 rounded-lg">
              <Loader2 size={13} className="animate-spin" />
              {rescoreProgress.done}/{rescoreProgress.total}
            </div>
          ) : isPreparing ? (
            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-lg">
              <Loader2 size={13} className="animate-spin" />
              {isVi ? 'Đang chuẩn bị bản thảo...' : 'Preparing drafts...'} {prepareProgress.done}/{prepareProgress.total}
            </div>
          ) : isCloudRunning ? (
            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-1.5 rounded-lg">
              <Loader2 size={13} className="animate-spin" />
              {isVi
                ? `Cloud đang chuẩn bị ${cloudJob!.total} bản thảo...`
                : `Cloud preparing ${cloudJob!.total} drafts...`}
            </div>
          ) : someBranchesChecked ? (
            <div className="flex items-center gap-2">
              {checkedUnscoredCount > 0 && (
                <button
                  onClick={handleScoreChecked}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                >
                  <RefreshCw size={12} />
                  {isVi ? `Score ${checkedUnscoredCount}` : `Score ${checkedUnscoredCount}`}
                </button>
              )}
              {checkedPreparableCount > 0 && (
                <button
                  onClick={handlePrepareDrafts}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
                >
                  <Zap size={12} />
                  {isVi ? `Chuẩn bị ${checkedPreparableCount} bản thảo` : `Prep ${checkedPreparableCount} drafts`}
                </button>
              )}
            </div>
          ) : totalUnscoredAll > 0 ? (
            <button
              onClick={handleScoreAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700/60 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
              <RefreshCw size={12} />
              {isVi ? `Score toàn bộ (${totalUnscoredAll})` : `Score all (${totalUnscoredAll})`}
            </button>
          ) : null}
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main tabs */}
      <div className="flex gap-0 px-6 border-b dark:border-slate-700 shrink-0">
        {(['manager', 'schedule'] as MainTab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setMainTab(tab)}
            className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              mainTab === tab
                ? 'text-indigo-600 dark:text-indigo-400 border-indigo-500'
                : 'text-gray-400 dark:text-gray-500 border-transparent hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {tab === 'manager' ? (isVi ? 'Quản lý' : 'Manager') : (isVi ? 'Lịch đăng' : 'Schedule')}
          </button>
        ))}
      </div>

      {rescoreError && (
        <div className="px-6 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 shrink-0">
          {rescoreError}
        </div>
      )}

      {/* Lịch đăng tab */}
      {mainTab === 'schedule' && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex justify-end max-w-2xl mx-auto mb-3">
            <button
              onClick={loadScheduledPosts}
              disabled={loadingScheduled}
              className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={12} className={loadingScheduled ? 'animate-spin' : ''} />
              {isVi ? 'Làm mới' : 'Refresh'}
            </button>
          </div>
          {loadingScheduled ? (
            <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
              <Loader2 size={24} className="animate-spin text-indigo-500" />
              <p className="text-sm">{isVi ? 'Đang tải...' : 'Loading...'}</p>
            </div>
          ) : scheduledPosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-300 dark:text-gray-600">
              <Calendar size={36} />
              <p className="text-sm">{isVi ? 'Chưa có bài nào được lên lịch.' : 'No scheduled posts yet.'}</p>
              <p className="text-xs text-gray-200 dark:text-gray-700">{isVi ? 'Chọn một ghi chú và nhấn "Lên lịch" để bắt đầu.' : 'Pick a note and click "Schedule" to get started.'}</p>
            </div>
          ) : (
            <div className="space-y-3 max-w-2xl mx-auto">
              {scheduledPosts.filter(p => p.status !== 'cancelled').map(post => {
                const isPending = post.status === 'pending';
                const isPosted = post.status === 'posted';
                const isFailed = post.status === 'failed';
                const isConfirming = confirmCancelId === post.id;
                const statusColor = isPosted ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                  : isFailed ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  : 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700';
                const statusLabel = isPosted ? (isVi ? 'Đã đăng' : 'Posted')
                  : isFailed ? (isVi ? 'Thất bại' : 'Failed')
                  : (isVi ? 'Chờ đăng' : 'Pending');
                return (
                  <div key={post.id} className="border dark:border-slate-700 rounded-2xl p-4 bg-white dark:bg-slate-900 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 leading-relaxed">{post.postText}</p>
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full border ${statusColor}`}>{statusLabel}</span>
                    </div>
                    {post.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {post.hashtags.map((h, i) => (
                          <span key={i} className="text-[10px] text-indigo-500 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded-full">{h.startsWith('#') ? h : `#${h}`}</span>
                        ))}
                      </div>
                    )}
                    {/* Inline reschedule editor */}
                    {isPending && editingScheduleId === post.id && (
                      <div className="flex items-center gap-2 pt-1">
                        <input
                          type="datetime-local"
                          value={editScheduleValue}
                          min={new Date(Date.now() + 60000).toISOString().slice(0,16)}
                          onChange={e => setEditScheduleValue(e.target.value)}
                          className="flex-1 text-xs border dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        <button
                          onClick={() => handleSaveSchedule(post.id)}
                          disabled={savingScheduleId === post.id}
                          className="flex items-center gap-1 text-[11px] font-semibold text-white bg-indigo-500 hover:bg-indigo-600 px-2.5 py-1.5 rounded-lg disabled:opacity-50"
                        >
                          {savingScheduleId === post.id ? <Loader2 size={10} className="animate-spin" /> : null}
                          {isVi ? 'Lưu' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingScheduleId(null)}
                          className="text-[11px] text-gray-400 hover:text-gray-600 px-2 py-1.5"
                        >
                          {isVi ? 'Bỏ qua' : 'Cancel'}
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <Clock size={11} />
                        {isPending
                          ? (isVi ? 'Sẽ đăng lúc: ' : 'Will post at: ')
                          : isPosted
                          ? (isVi ? 'Đã đăng lúc: ' : 'Posted at: ')
                          : (isVi ? 'Lên lịch lúc: ' : 'Scheduled at: ')}
                        {new Date(post.scheduledAt).toLocaleString()}
                        {isPending && editingScheduleId !== post.id && (
                          <button
                            onClick={() => handleStartEditSchedule(post)}
                            className="ml-1 text-gray-300 hover:text-indigo-500 dark:text-gray-600 dark:hover:text-indigo-400 transition-colors"
                            title={isVi ? 'Đổi giờ đăng' : 'Change post time'}
                          >
                            <Pencil size={10} />
                          </button>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPreviewPost(post)}
                          className="flex items-center gap-1 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors"
                        >
                          <Eye size={11} />
                          {isVi ? 'Xem trước' : 'Preview'}
                        </button>
                        {isPosted && post.postUrl && (
                          <a href={post.postUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-indigo-500 hover:text-indigo-600">
                            <ExternalLink size={11} />
                            {isVi ? 'Xem bài' : 'View post'}
                          </a>
                        )}
                        {isFailed && (
                          <>
                            {post.failReason && (
                              <span className="text-red-400 truncate max-w-[160px]" title={post.failReason}>
                                {post.failReason.includes('requested resource') || post.failReason.includes('not connected') || post.failReason.includes('expired')
                                  ? (isVi ? 'Token hết hạn' : 'Token expired')
                                  : post.failReason}
                              </span>
                            )}
                            <button
                              onClick={() => handleRetryPost(post.id)}
                              disabled={retryingId === post.id}
                              className="flex items-center gap-1 text-indigo-500 hover:text-indigo-600 font-medium disabled:opacity-50 transition-colors"
                            >
                              {retryingId === post.id
                                ? <Loader2 size={11} className="animate-spin" />
                                : <RefreshCw size={11} />}
                              {isVi ? 'Đăng lại' : 'Retry'}
                            </button>
                          </>
                        )}
                        {isPending && !isConfirming && (
                          <button
                            onClick={() => setConfirmCancelId(post.id)}
                            className="flex items-center gap-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={11} />
                            {isVi ? 'Hủy' : 'Cancel'}
                          </button>
                        )}
                        {isPending && isConfirming && (
                          <>
                            <span className="text-red-500 dark:text-red-400 font-medium">{isVi ? 'Xóa?' : 'Delete?'}</span>
                            <button
                              onClick={() => handleCancelPost(post.id)}
                              disabled={cancellingId === post.id}
                              className="flex items-center gap-1 text-red-500 hover:text-red-600 font-semibold disabled:opacity-50"
                            >
                              {cancellingId === post.id ? <Loader2 size={11} className="animate-spin" /> : null}
                              {isVi ? 'Xác nhận' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setConfirmCancelId(null)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              {isVi ? 'Không' : 'No'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Three-panel body — manager tab */}
      {mainTab === 'manager' && <div className="flex flex-1 overflow-hidden">

        {/* Left panel — Notebook selector + Branch list with checkboxes */}
        <div className="w-64 shrink-0 border-r dark:border-slate-700 flex flex-col overflow-hidden">

          {/* Notebook selector */}
          {allNotebooksMeta.length > 1 && (
            <div className="px-3 py-2 border-b dark:border-slate-800 shrink-0 bg-gray-50/80 dark:bg-slate-950/60">
              <p className="text-[9px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-1">{isVi ? 'Notebook' : 'Notebook'}</p>
              <select
                value={activeNotebookMeta.id}
                onChange={e => {
                  const meta = allNotebooksMeta.find(nb => nb.id === e.target.value);
                  if (meta) loadNotebook(meta);
                }}
                className="w-full text-xs bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg px-2 py-1.5 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 truncate"
              >
                {allNotebooksMeta.map(nb => (
                  <option key={nb.id} value={nb.id}>{nb.title}</option>
                ))}
              </select>
              {isLoadingNodes && (
                <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-indigo-500 dark:text-indigo-400">
                  <Loader2 size={10} className="animate-spin" />
                  {isVi ? 'Đang tải ghi chú...' : 'Loading notes...'}
                </div>
              )}
            </div>
          )}

          {/* Select all row */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b dark:border-slate-800 shrink-0 bg-gray-50/50 dark:bg-slate-900/50">
            <input
              type="checkbox"
              checked={allBranchesChecked}
              onChange={() => {
                if (allBranchesChecked) {
                  setCheckedBranchIds(new Set());
                } else {
                  setCheckedBranchIds(new Set(branches.map(b => b.id)));
                }
              }}
              className="w-3.5 h-3.5 rounded accent-indigo-600 cursor-pointer"
            />
            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              {isVi ? 'Chương / Nhánh' : 'Chapters / Branches'}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {branches.length === 0 ? (
              <p className="px-4 py-6 text-xs text-gray-400 text-center">{isVi ? 'Không có nhánh nào.' : 'No branches found.'}</p>
            ) : (
              branches.map(branch => {
                const notes = branchDescendants.get(branch.id) ?? [];
                const scored = notes.filter(n => n.threadsAssessedAt != null);
                const unscored = notes.filter(n => !n.threadsAssessedAt);
                const avg = scored.length > 0
                  ? Math.round(scored.reduce((s, n) => s + (n.threadsScore ?? 0), 0) / scored.length)
                  : null;
                const isSelected = selectedBranchId === branch.id;
                const isChecked = checkedBranchIds.has(branch.id);

                return (
                  <div
                    key={branch.id}
                    onClick={() => { setSelectedBranchId(branch.id); setNoteTab('all'); setCheckedNoteIds(new Set()); }}
                    className={`flex items-start gap-2 px-3 py-2.5 cursor-pointer transition-colors border-b dark:border-slate-800/50 border-l-2 ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-indigo-500'
                        : 'hover:bg-gray-50 dark:hover:bg-slate-800/40 border-l-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}}
                      onClick={e => toggleBranchCheck(branch.id, e)}
                      className="w-3.5 h-3.5 rounded accent-indigo-600 cursor-pointer mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium leading-snug line-clamp-2 ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                        {branch.title || '(untitled)'}
                      </p>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">{notes.length} {isVi ? 'ghi chú' : 'notes'}</span>
                        {avg != null && (
                          <span className={`text-[10px] font-semibold ${avg >= 70 ? 'text-emerald-600 dark:text-emerald-400' : avg >= 50 ? 'text-amber-500' : 'text-gray-400'}`}>avg {avg}</span>
                        )}
                        {unscored.length > 0 && (
                          <span className="text-[10px] text-gray-300 dark:text-gray-600">{unscored.length} unscored</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel — Notes */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!selectedBranch ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-300 dark:text-gray-600">
              <Sparkles size={36} />
              <p className="text-sm">{isVi ? 'Chọn một chương từ bên trái' : 'Select a chapter from the left'}</p>
              <p className="text-xs text-gray-200 dark:text-gray-700">{isVi ? 'Hoặc tick checkbox để score nhiều nhánh cùng lúc' : 'Or check branches to score multiple at once'}</p>
            </div>
          ) : (
            <>
              {/* Branch header */}
              <div className="flex items-center justify-between px-5 py-3 border-b dark:border-slate-700 shrink-0 bg-gray-50/50 dark:bg-slate-900/50">
                <div>
                  <p className="text-sm font-semibold dark:text-white truncate max-w-md">{selectedBranch.title}</p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    {branchNotes.length} {isVi ? 'ghi chú' : 'notes'}
                    {scoredNotes.length > 0 && ` · ${scoredNotes.length} scored`}
                    {unscoredNotes.length > 0 && ` · ${unscoredNotes.length} unscored`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!isRescoring && unscoredNotes.length > 0 && checkedNoteIds.size === 0 && (
                    <button
                      onClick={() => handleScoreBranch(selectedBranch.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700/60 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                    >
                      <RefreshCw size={11} />
                      {isVi ? `Score ${unscoredNotes.length}` : `Score ${unscoredNotes.length}`}
                    </button>
                  )}
                  {!isPreparing && checkedNoteIds.size > 0 && (
                    <button
                      onClick={handlePrepareCheckedNotes}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
                    >
                      <Zap size={11} />
                      {isVi ? `Chuẩn bị ${checkedNoteIds.size} bản thảo` : `Prep ${checkedNoteIds.size} drafts`}
                    </button>
                  )}
                  {isPreparing && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                      <Loader2 size={12} className="animate-spin" />
                      {prepareProgress.done}/{prepareProgress.total}
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-0 px-5 border-b dark:border-slate-700 shrink-0">
                {noteTabs.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setNoteTab(t.key)}
                    className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                      noteTab === t.key
                        ? 'text-indigo-600 dark:text-indigo-400 border-indigo-500'
                        : 'text-gray-400 dark:text-gray-500 border-transparent hover:text-gray-600 dark:hover:text-gray-300'
                    }`}
                  >
                    {t.label}<span className="ml-1 text-[10px] opacity-60">({t.count})</span>
                  </button>
                ))}
              </div>

              {/* Note list select-all row */}
              {displayNotes.length > 0 && (
                <div className="flex items-center gap-2 px-5 py-1.5 border-b dark:border-slate-800 bg-gray-50/30 dark:bg-slate-900/30 shrink-0">
                  <input
                    type="checkbox"
                    checked={displayNotes.length > 0 && displayNotes.every(n => checkedNoteIds.has(n.id))}
                    onChange={() => {
                      const allChecked = displayNotes.every(n => checkedNoteIds.has(n.id));
                      setCheckedNoteIds(prev => {
                        const next = new Set(prev);
                        displayNotes.forEach(n => allChecked ? next.delete(n.id) : next.add(n.id));
                        return next;
                      });
                    }}
                    className="w-3.5 h-3.5 rounded accent-amber-500 cursor-pointer"
                  />
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {checkedNoteIds.size > 0 ? (isVi ? `${checkedNoteIds.size} đã chọn` : `${checkedNoteIds.size} selected`) : (isVi ? 'Chọn tất cả' : 'Select all')}
                  </span>
                </div>
              )}

              {/* Note list */}
              <div className="flex-1 overflow-y-auto">
                {displayNotes.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-300 dark:text-gray-600">
                    <AlertCircle size={28} />
                    <p className="text-xs">
                      {noteTab === 'unscored' ? (isVi ? 'Tất cả đã được score.' : 'All notes scored.')
                        : noteTab === 'posted' ? (isVi ? 'Chưa post gì.' : 'Nothing posted yet.')
                        : noteTab === 'recommended' ? (isVi ? 'Chưa có note đủ điểm.' : 'No high-scored notes yet.')
                        : (isVi ? 'Không có ghi chú.' : 'No notes.')}
                    </p>
                    {noteTab === 'recommended' && unscoredNotes.length > 0 && !isRescoring && (
                      <button onClick={() => handleScoreBranch(selectedBranch.id)} className="text-xs text-indigo-500 hover:underline mt-1">
                        {isVi ? `Score ${unscoredNotes.length} ghi chú` : `Score ${unscoredNotes.length} notes`}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y dark:divide-slate-800/50">
                    {displayNotes.map(node => (
                      <NoteRow
                        key={node.id}
                        node={node}
                        isVi={isVi}
                        isScoring={scoringNodeId === node.id}
                        hasDraft={!!(node.threadsPostDraft && node.threadsPostDraftAt && Date.now() - node.threadsPostDraftAt < 7 * 24 * 60 * 60 * 1000)}
                        isChecked={checkedNoteIds.has(node.id)}
                        onToggleCheck={() => setCheckedNoteIds(prev => { const next = new Set(prev); next.has(node.id) ? next.delete(node.id) : next.add(node.id); return next; })}
                        onPost={() => handleGenerateDraft(node)}
                        onOpenNote={onOpenNote ? () => onOpenNote(node.id) : undefined}
                        onScore={!isRescoring ? () => scoreNotes([node]) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right panel — AI Advisor */}
        {(() => {
          const notesContext: AdvisorNoteContext[] = allNodes
            .filter(n => n.parentId)
            .map(n => ({
              title: n.title || '',
              score: n.threadsScore ?? null,
              tags: n.tags ?? [],
              status: n.threadsStatus || 'none',
              threadsStatus: n.threadsStatus,
            }));

          const selectedNodesList = allNodes.filter(n => checkedNoteIds.has(n.id));
          const selectedForAdvisor: AdvisorSelectedNote[] = selectedNodesList.map(n => ({
            title: n.title || '',
            content: n.content || '',
            score: n.threadsScore ?? null,
            correctnessScore: n.correctnessScore ?? null,
          }));

          const sendMessage = async (overrideText?: string) => {
            const text = (overrideText ?? advisorInput).trim();
            if (!text || advisorLoading) return;
            const userMsg: AdvisorMessage = { role: 'user', text };
            const nextMsgs = [...advisorMessages, userMsg];
            setAdvisorMessages(nextMsgs);
            setAdvisorInput('');
            setAdvisorLoading(true);
            try {
              const reply = await threadsAdvisorChat(
                nextMsgs, notesContext, language,
                selectedForAdvisor.length > 0 ? selectedForAdvisor : undefined
              );
              setAdvisorMessages(prev => [...prev, { role: 'model', text: reply }]);
            } catch {
              setAdvisorMessages(prev => [...prev, { role: 'model', text: isVi ? '❌ Có lỗi xảy ra. Thử lại nhé.' : '❌ Something went wrong. Please try again.' }]);
            } finally {
              setAdvisorLoading(false);
              setTimeout(() => advisorBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            }
          };

          const starterPrompts = isVi
            ? ['Nên đăng note nào tuần này?', 'Lên kế hoạch 5 bài tốt nhất', 'Note nào cần cải thiện?']
            : ['Which notes should I post this week?', 'Plan my top 5 posts', 'Which notes need work?'];

          return (
            <div className="w-80 shrink-0 border-l dark:border-slate-700 flex flex-col overflow-hidden bg-gray-50/50 dark:bg-slate-900/50">
              {/* Header */}
              <div className="px-4 py-3 border-b dark:border-slate-700 shrink-0 flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Bot size={13} className="text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">AI Advisor</span>
                <span className="ml-auto text-[10px] text-indigo-400">
                  {notesContext.filter(n => n.score != null && n.score >= 75).length} {isVi ? 'sẵn sàng' : 'ready'}
                </span>
              </div>
              {/* Selected notes indicator */}
              {selectedForAdvisor.length > 0 && (
                <div className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-100 dark:border-indigo-800/40 shrink-0 flex items-center gap-2">
                  <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium flex-1">
                    {isVi ? `${selectedForAdvisor.length} ghi chú đang được chọn` : `${selectedForAdvisor.length} note${selectedForAdvisor.length > 1 ? 's' : ''} selected`}
                  </span>
                  <button
                    onClick={() => sendMessage(isVi ? 'Phân tích các ghi chú tôi đã chọn. Chúng có tốt để đăng không?' : 'Analyze the notes I selected. Are they good to post?')}
                    disabled={advisorLoading}
                    className="text-[10px] font-bold px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-40 transition-colors whitespace-nowrap"
                  >
                    {isVi ? 'Phân tích ngay' : 'Analyze'}
                  </button>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {advisorMessages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-2">
                    <p className="text-xs text-gray-400 dark:text-gray-500">{isVi ? 'Hỏi tôi về kế hoạch đăng bài của bạn.' : 'Ask me about your posting plan.'}</p>
                    <div className="flex flex-col gap-1.5 w-full">
                      {starterPrompts.map(p => (
                        <button key={p} onClick={() => { setAdvisorInput(p); advisorInputRef.current?.focus(); }}
                          className="w-full text-left px-3 py-2 text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-600 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {advisorMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'model' && (
                      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 mr-1.5 mt-0.5">
                        <Bot size={11} className="text-white" />
                      </div>
                    )}
                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-sm'
                        : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 rounded-bl-sm shadow-sm'
                    }`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
                {advisorLoading && (
                  <div className="flex justify-start">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 mr-1.5">
                      <Bot size={11} className="text-white" />
                    </div>
                    <div className="px-3 py-2 rounded-2xl bg-white dark:bg-slate-800 flex items-center gap-1 shadow-sm">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                )}
                <div ref={advisorBottomRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-3 border-t dark:border-slate-700 shrink-0">
                <div className="flex gap-1.5 items-end">
                  <textarea
                    ref={advisorInputRef}
                    value={advisorInput}
                    onChange={e => setAdvisorInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    rows={2}
                    placeholder={isVi ? 'Nhắn tin...' : 'Message...'}
                    className="flex-1 resize-none text-xs border border-gray-200 dark:border-slate-600 rounded-xl px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 placeholder-gray-300 dark:placeholder-gray-600"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!advisorInput.trim() || advisorLoading}
                    className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl disabled:opacity-40 transition-colors shrink-0"
                  >
                    <Send size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>}

      {/* Post preview overlay */}
      {previewPost && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-10 p-6">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
            {/* Preview header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b dark:border-slate-700 shrink-0">
              <p className="text-sm font-semibold dark:text-white flex items-center gap-2">
                <Eye size={14} className="text-indigo-500" />
                {isVi ? 'Xem trước bài đăng' : 'Post Preview'}
              </p>
              <button onClick={() => setPreviewPost(null)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg">
                <X size={16} />
              </button>
            </div>

            {/* Threads-style post mock */}
            <div className="overflow-y-auto p-5 space-y-4">
              {/* Profile row */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles size={14} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold dark:text-white">@you</span>
                    <span className="text-[11px] text-gray-400">{isVi ? 'sẽ đăng lúc' : 'posts at'} {new Date(previewPost.scheduledAt).toLocaleString()}</span>
                  </div>
                  {/* Post text */}
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{previewPost.postText}</p>

                  {/* Hashtags */}
                  {previewPost.hashtags?.length > 0 && (
                    <p className="mt-2 text-sm text-indigo-500 dark:text-indigo-400">
                      {previewPost.hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ')}
                    </p>
                  )}

                  {/* Images */}
                  {previewPost.imageUrls && previewPost.imageUrls.length > 0 && (
                    <div className={`mt-3 grid gap-1.5 rounded-xl overflow-hidden ${previewPost.imageUrls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {previewPost.imageUrls.slice(0, 4).map((url, i) => (
                        <img key={i} src={url} alt="" className="w-full object-cover rounded aspect-square" />
                      ))}
                    </div>
                  )}

                  {/* Note link */}
                  {previewPost.noteLink ? (
                    <div className="mt-3 border dark:border-slate-700 rounded-xl p-3 bg-gray-50 dark:bg-slate-800 flex items-center gap-2">
                      <ExternalLink size={12} className="text-indigo-500 shrink-0" />
                      <span className="text-[11px] text-indigo-500 dark:text-indigo-400 truncate">{previewPost.noteLink}</span>
                    </div>
                  ) : (
                    <div className="mt-3 border dark:border-slate-700 rounded-xl p-3 bg-gray-50 dark:bg-slate-800 flex items-center gap-2">
                      <ExternalLink size={12} className="text-gray-300 dark:text-gray-600 shrink-0" />
                      <span className="text-[11px] text-gray-300 dark:text-gray-600 italic truncate">{isVi ? 'Không có link (notebook chưa publish lên GitHub)' : 'No link (notebook not published to GitHub)'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Char count — counts full post: text + link + hashtags */}
              {(() => {
                const hashtagStr = (previewPost.hashtags || []).map(h => h.startsWith('#') ? h : `#${h}`).join(' ');
                const parts = [previewPost.postText];
                if (previewPost.noteLink) parts.push(previewPost.noteLink);
                if (hashtagStr) parts.push(hashtagStr);
                const total = parts.join('\n\n').length;
                return (
                  <div className="flex items-center justify-end gap-2">
                    {previewPost.noteLink && <span className="text-[10px] text-gray-300 dark:text-gray-600">incl. link + hashtags</span>}
                    <span className={`text-[11px] font-mono font-semibold ${total > 500 ? 'text-red-500' : total > 450 ? 'text-amber-500' : 'text-gray-400'}`}>
                      {total} / 500
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AiCheckBadge({ analysis, isVi }: { analysis: import('../types').AIAnalysis | null; isVi: boolean }) {
  if (!analysis) return (
    <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-gray-300 dark:text-gray-600 bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-full">
      AI?
    </span>
  );
  const color = analysis.isAccurate
    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
    : 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${color}`}>
      {analysis.isAccurate ? '✓' : '✗'} {analysis.score}
    </span>
  );
}

function NoteRow({ node, isVi, isScoring, hasDraft, isChecked, onToggleCheck, onPost, onOpenNote, onScore }: {
  node: MindMapNode; isVi: boolean; isScoring: boolean; hasDraft: boolean;
  isChecked: boolean; onToggleCheck: () => void; onPost: () => void; onOpenNote?: () => void; onScore?: () => void;
}) {
  const aiScore = node.correctnessScore;
  const aiOk = node.correctnessAccurate;
  const aiChecked = node.correctnessScore != null;

  return (
    <div className={`flex items-center gap-3 px-5 py-3.5 transition-colors group ${isChecked ? 'bg-amber-50/40 dark:bg-amber-900/10' : isScoring ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : 'hover:bg-gray-50 dark:hover:bg-slate-800/40'}`}>

      {/* Checkbox */}
      <input
        type="checkbox"
        checked={isChecked}
        onChange={onToggleCheck}
        onClick={e => e.stopPropagation()}
        className="w-3.5 h-3.5 rounded accent-amber-500 cursor-pointer shrink-0"
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium dark:text-white truncate leading-snug flex-1 min-w-0">
            {node.title || (isVi ? '(chưa đặt tên)' : '(untitled)')}
          </p>
          {onOpenNote && (
            <button
              onClick={e => { e.stopPropagation(); onOpenNote(); }}
              title={isVi ? 'Mở ghi chú' : 'Open note'}
              className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              <ExternalLink size={11} />
            </button>
          )}
        </div>
        {node.threadsScoreReason && !isScoring && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1">{node.threadsScoreReason}</p>
        )}
        {node.bestAngle && !isScoring && (
          <p className="text-[11px] text-indigo-500 dark:text-indigo-400 mt-0.5 flex items-center gap-1 line-clamp-1">
            <Lightbulb size={10} className="shrink-0" />
            {node.bestAngle.replace(/_/g, ' ')}
            {node.threadsScore != null && node.bestAngleScore != null && node.bestAngleScore !== node.threadsScore && (
              <span className="text-gray-400 dark:text-gray-500 font-normal">· generic {node.threadsScore}</span>
            )}
          </p>
        )}
        {!node.bestAngle && node.threadsScoreAngle && (node.threadsScore ?? 0) >= 60 && !isScoring && (
          <p className="text-[11px] text-indigo-500 dark:text-indigo-400 mt-0.5 flex items-center gap-1 line-clamp-1">
            <Lightbulb size={10} className="shrink-0" />
            {node.threadsScoreAngle}
          </p>
        )}
        {isScoring && <p className="text-[11px] text-indigo-500 dark:text-indigo-400 mt-0.5 italic">Đang đánh giá...</p>}
        {!node.threadsAssessedAt && !isScoring && (
          <p className="text-[11px] text-gray-300 dark:text-gray-600 mt-0.5 italic">{isVi ? 'Chưa được đánh giá' : 'Not yet scored'}</p>
        )}
        {node.threadsPostedAt && (
          <p className="text-[10px] text-emerald-500 dark:text-emerald-400 mt-0.5 flex items-center gap-1">
            <CheckCircle2 size={10} />
            {isVi ? 'Đã post' : 'Posted'} {new Date(node.threadsPostedAt).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* 2 scores + Post button */}
      {!isScoring ? (
        <div className="shrink-0 flex items-center gap-3">
          {/* Postworthy / Best Angle Score */}
          <div className="flex flex-col items-center gap-0.5 w-16">
            <span className="text-[9px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-wider leading-none">
              {node.bestAngle ? 'Best' : 'Postworthy'}
            </span>
            {(() => {
              const displayScore = node.bestAngleScore != null ? node.bestAngleScore : node.threadsScore;
              return displayScore != null
                ? <span className={`text-lg font-bold font-mono leading-tight ${displayScore >= 70 ? 'text-emerald-600 dark:text-emerald-400' : displayScore >= 50 ? 'text-amber-500' : 'text-red-400'}`}>{displayScore}</span>
                : <span className="text-base text-gray-300 dark:text-gray-600">—</span>;
            })()}
          </div>
          {/* AI Correctness */}
          <div className="flex flex-col items-center gap-0.5 w-16">
            <span className="text-[9px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-wider leading-none">Correctness</span>
            {aiChecked
              ? <span className={`text-lg font-bold font-mono leading-tight ${aiOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>{aiScore}</span>
              : <span className="text-base text-gray-300 dark:text-gray-600">—</span>
            }
          </div>
          {/* AI Check (from expand modal) */}
          <div className="flex flex-col items-center gap-0.5 w-16">
            <span className="text-[9px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-wider leading-none">AI Check</span>
            {node.analysis != null
              ? <span className={`text-lg font-bold font-mono leading-tight ${node.analysis.isAccurate ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>{node.analysis.score}</span>
              : <span className="text-base text-gray-300 dark:text-gray-600">—</span>
            }
          </div>
          {/* Draft indicator */}
          <div className="flex flex-col items-center gap-0.5 w-10">
            <span className="text-[9px] font-bold text-gray-300 dark:text-gray-600 uppercase tracking-wider leading-none">Draft</span>
            {hasDraft
              ? <Zap size={14} className="text-amber-500" />
              : <span className="text-base text-gray-200 dark:text-gray-700">—</span>
            }
          </div>
          {/* Score button */}
          {onScore && (
            <button
              onClick={e => { e.stopPropagation(); onScore(); }}
              title={isVi ? 'Score ghi chú này' : 'Score this note'}
              className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors shrink-0 ${
                !node.threadsAssessedAt
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                  : 'text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
              }`}
            >
              <RefreshCw size={12} />
            </button>
          )}
          {/* Post button */}
          {!node.threadsPostedAt && (
            <button
              onClick={onPost}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                hasDraft
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : (node.bestAngleScore ?? node.threadsScore ?? 0) >= 60
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'border dark:border-slate-700 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'
              }`}
            >
              {hasDraft ? <Zap size={10} /> : <Send size={10} />}
              Post
            </button>
          )}
        </div>
      ) : (
        <Loader2 size={14} className="animate-spin text-indigo-500 shrink-0" />
      )}
    </div>
  );
}
